import { createSelectorCreator, lruMemoize } from 'reselect';

import { type FacetCollection, FacetValue, SearchItemDataCollection } from 'Models/index';
import { comparerBy } from 'Utils/array';

export const createFacetCollectionSelector = createSelectorCreatorForComparable<FacetCollection>(
  (prev, next) => prev.sameFacetsAsIn(next),
);

export const createSearchItemDataCollectionSelector = createSelectorCreatorForComparable(
  (prev: SearchItemDataCollection, next: SearchItemDataCollection) => prev.equals(next),
);

export const createFacetValueArraySelector = createSelectorCreatorForComparable(
  comparerBy(FacetValue.valueKey),
);

function createSelectorCreatorForComparable<T>(comparer: (prev: T, next: T) => boolean) {
  return createSelectorCreator(lruMemoize, {
    resultEqualityCheck: (prev: T, next: T) => areComparableResultsEqual(comparer, prev, next),
  });
}

function areComparableResultsEqual<T>(comparer: (prev: T, next: T) => boolean, prev: T, next: T): boolean {
  try {
    return comparer(prev, next);
  } catch {
    return false;
  }
}

export type IsolatedCreator<R> = (isolatedKey?: string | null) => R;

type CreatorResults<TCreators extends readonly IsolatedCreator<unknown>[]> = {
  [K in keyof TCreators]: TCreators[K] extends IsolatedCreator<infer TResult> ? TResult : never;
};

type IsolatedCombiner<Selectors extends readonly unknown[], R> = (
  selectors: Selectors,
  isolatedKey: string | null,
) => R;

// Array-first call: createIsolatedSelectorCreator([c1, c2, ...], combiner)

export function createIsolatedSelectorCreator<const TCreators extends readonly IsolatedCreator<unknown>[], R>(
  creators: TCreators,
  combiner: IsolatedCombiner<CreatorResults<TCreators>, R>,
): IsolatedCreator<R>;

// Variadic spread fallback: createIsolatedSelectorCreator(...creators, combiner)
// eslint-disable-next-line no-redeclare
export function createIsolatedSelectorCreator<const TCreators extends readonly IsolatedCreator<unknown>[], R>(
  ...args: [...TCreators, IsolatedCombiner<CreatorResults<TCreators>, R>]
): IsolatedCreator<R>;

// eslint-disable-next-line no-redeclare
export function createIsolatedSelectorCreator<TReturn>(...args: unknown[]): IsolatedCreator<TReturn> {
  const isolatedSelectorCreators = Array.isArray(args[0]) ? args[0] : args.slice(0, -1);
  const combiner = args[args.length - 1];

  if (!isIsolatedCreatorArray(isolatedSelectorCreators) || !isUnknownCombiner<TReturn>(combiner)) {
    throw new TypeError('Invalid isolated selector creator arguments.');
  }

  const selectors = new Map<string, TReturn>();
  return (isolatedKey: string | null = null): TReturn => {
    const key = isolatedKey ?? '__null__';
    if (!selectors.has(key)) {
      selectors.set(
        key,
        combiner(
          isolatedSelectorCreators.map((creator) => creator(isolatedKey)),
          isolatedKey,
        ),
      );
    }
    return selectors.get(key)!;
  };
}

function isIsolatedCreator(value: unknown): value is IsolatedCreator<unknown> {
  return typeof value === 'function';
}

function isIsolatedCreatorArray(values: unknown[]): values is IsolatedCreator<unknown>[] {
  return values.every(isIsolatedCreator);
}

function isUnknownCombiner<TReturn>(
  value: unknown,
): value is (selectorFactories: unknown[], isolatedKey: string | null) => TReturn {
  return typeof value === 'function';
}
